import { tabsAnatomy } from '@chakra-ui/react/anatomy';
import { defineSlotRecipe } from '@chakra-ui/react/styled-system';

import { rem } from '../themeUtils';

// The trigger's own icon reads this variable. An `_icon` rule would also recolour the canvas close
// button and any icon inside a badge.
// These helpers stay unannotated: spreading a `SystemStyleObject` into the recipe makes its inferred
// type too large for the declaration build to emit (TS7056).
const iconColor = (token: string) => ({ '--tabs-icon-color': `colors.${token}` });

const containedActive = {
  ...iconColor('icon/secondary'),
  backgroundColor: 'background/primary',
  color: 'text/primary',
  _after: { backgroundColor: 'border/regular' },
};

const containedSelected = {
  ...iconColor('icon/interactive'),
  backgroundColor: 'background/primary',
  color: 'text/primary',
};

const tabsSlotRecipe = defineSlotRecipe({
  // `moreTrigger` is a custom slot (not in the Chakra anatomy) for the overflow "N more…" button.
  slots: [...tabsAnatomy.keys(), 'moreTrigger'],
  className: 'chakra-tabs',
  base: {
    list: {
      display: 'flex',
    },
    trigger: {
      '--focus-ring-width': rem(2),
      ...iconColor('icon/tertiary'),
      color: 'text/secondary',
      cursor: 'pointer',
      textStyle: 'body/lg/semibold',
      paddingInline: '16',
      position: 'relative',
      textAlign: 'left',
      whiteSpace: 'nowrap',
      zIndex: 0,
      _disabled: {
        cursor: 'not-allowed',
      },
      _selected: {
        zIndex: 1,
        borderColor: 'border/selected',
        _hover: {
          borderColor: 'border/selected',
        },
      },
    },
    content: {
      outline: 'none',
    },
    // Overflow "N more…" button — variant-specific look is layered on per variant below.
    moreTrigger: {
      '--focus-ring-width': rem(2),
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      flexShrink: 0,
      whiteSpace: 'nowrap',
      cursor: 'pointer',
      color: 'text/secondary',
    },
  },

  variants: {
    variant: {
      line: {
        list: {
          borderBlockEnd: '1px solid',
          borderColor: 'border/regular',
          // On mobile the tabs share the row, but only while all of them fit: once the "more" button
          // shows up they keep their content width, as they always do from tablet up.
          // Child combinators, because the inner tablist gets this slot's class too and never
          // carries the attribute.
          '&:not([data-overflowing]) > .chakra-tabs__list > .chakra-tabs__trigger': {
            flex: { baseOnly: '1' },
            justifyContent: { baseOnly: 'center' },
          },
        },
        trigger: {
          bottom: '-1px',
          display: 'flex',
          alignItems: 'center',
          gap: '12',
          paddingBlockStart: '12',
          paddingBlockEnd: rem(9),
          borderBlockEnd: '2px solid transparent',
          _selected: {
            ...iconColor('icon/interactive'),
            color: 'text/primary',
            _hover: iconColor('icon/interactive'),
          },
          _hover: {
            ...iconColor('icon/secondary'),
            borderColor: 'border/hover',
            color: 'text/primary',
          },
          _disabled: {
            ...iconColor('icon/disabled'),
            color: 'text/disabled',
          },
        },
        moreTrigger: {
          bottom: '-1px',
          textStyle: 'body/lg/semibold',
          paddingInline: '16',
          paddingBlockStart: '12',
          paddingBlockEnd: rem(9),
          borderBlockEnd: '2px solid transparent',
          _hover: { backgroundColor: 'background/hover' },
          _open: { backgroundColor: 'background/active', color: 'text/primary' },
        },
      },
      contained: {
        trigger: {
          display: 'flex',
          alignItems: 'flex-start',
          gap: '16',
          paddingBlockStart: rem(10),
          paddingBlockEnd: '12',
          backgroundColor: 'background/tertiary',
          borderBlockStart: '2px solid transparent',
          // Separator on the right edge. The last tab has none, and neither has the selected one,
          // which joins the panel below.
          _after: {
            content: '""',
            position: 'absolute',
            insetBlock: 0,
            insetInlineEnd: 0,
            width: '1',
            backgroundColor: 'border/regular',
          },
          '&:last-of-type': {
            _after: { display: 'none' },
          },
          _hover: {
            ...iconColor('icon/secondary'),
            backgroundColor: 'background/active',
            _after: { backgroundColor: 'border/strong' },
            _active: containedActive,
          },
          _active: containedActive,
          _disabled: {
            ...iconColor('icon/on-disabled'),
            backgroundColor: 'color/neutral/moderate',
            color: 'text/on-disabled',
            _after: { backgroundColor: 'border/strong' },
          },
          _selected: {
            ...containedSelected,
            _after: { display: 'none' },
            _hover: { ...containedSelected, _active: containedSelected },
            _active: containedSelected,
            _focusVisible: {
              _after: { display: 'block', backgroundColor: 'border/strong' },
            },
          },
        },
        moreTrigger: {
          // Regular weight (not the semibold tabs use) and no top marker — per the contained "more" design.
          textStyle: 'body/lg/regular',
          paddingInline: '16',
          paddingBlock: '12',
          backgroundColor: 'background/tertiary',
          _hover: { backgroundColor: 'background/active', color: 'text/primary' },
          _open: { backgroundColor: 'background/active', color: 'text/primary' },
        },
        contentGroup: {
          backgroundColor: 'background/primary',
          borderRadius: '4',
          borderStartStartRadius: 0,
        },
      },
      // IDE-style canvas tabs: fixed-height, edge-to-edge segments separated by vertical
      // dividers, with the selection marker on top. Closable + status-dot live only here.
      canvas: {
        list: {
          // Small leading gap before the first tab.
          paddingInlineStart: '12',
          borderBlockEnd: '1px solid',
          borderColor: 'border/regular',
        },
        trigger: {
          height: '40',
          display: 'flex',
          alignItems: 'center',
          gap: '4',
          textStyle: 'body/md/regular',
          backgroundColor: 'background/secondary',
          paddingInline: '12',
          paddingBlock: '8',
          // Bottom divider; shifted down 1px so it sits exactly on the list's bottom border (no
          // double line). The selected tab paints this white to merge with the content panel below.
          borderBlockEnd: '1px solid',
          bottom: '-1px',
          // Vertical divider between segments.
          borderInlineEnd: '1px solid',
          borderColor: 'border/regular',
          // The first tab also needs a leading divider (the others get theirs from the previous tab's
          // end). Set width/style only so the color comes from `borderColor` (border/regular), not the
          // shorthand's currentColor.
          '&:first-of-type': {
            borderInlineStartWidth: '1',
            borderInlineStartStyle: 'solid',
          },
          _hover: {
            backgroundColor: 'background/primary',
            color: 'text/primary',
          },
          _selected: {
            zIndex: 1,
            backgroundColor: 'background/primary',
            color: 'text/primary',
            // Keep neutral side dividers; the selected tab keeps a subtle bottom border because the
            // panel below it is secondary grey (not white), so it shouldn't fully merge.
            borderColor: 'border/regular',
            borderBlockEndColor: 'border/minimal',
            // Selection marker: an overlay bar on top of the selected tab only — non-selected
            // tabs have no top border, and it adds no layout height so nothing shifts.
            _after: {
              content: '""',
              position: 'absolute',
              insetBlockStart: 0,
              insetInline: 0,
              height: '2',
              backgroundColor: 'border/selected',
            },
            _hover: {
              backgroundColor: 'background/primary',
              borderColor: 'border/regular',
              borderBlockEndColor: 'border/minimal',
            },
          },
          _disabled: {
            ...iconColor('icon/disabled'),
            backgroundColor: 'color/neutral/moderate',
            color: 'text/on-disabled',
            _hover: {
              backgroundColor: 'color/neutral/moderate',
              color: 'text/on-disabled',
            },
          },
        },
        moreTrigger: {
          height: '40',
          textStyle: 'body/md/regular',
          paddingInline: '12',
          bottom: '-1px',
          borderBlockEnd: '1px solid',
          borderInlineEnd: '1px solid',
          borderColor: 'border/regular',
          _hover: { backgroundColor: 'background/hover', color: 'text/primary' },
          _open: { backgroundColor: 'background/active', color: 'text/primary' },
          _focusVisible: { outline: 'none', boxShadow: 'inset 0 0 0 var(--focus-ring-width) var(--focus-ring-color)' },
        },
        contentGroup: {
          backgroundColor: 'background/primary',
        },
      },
    },
    // Only the line variant comes in two sizes; contained and canvas have one each.
    size: {
      lg: {},
      md: {},
    },
  },

  compoundVariants: [
    {
      variant: 'line',
      size: 'md',
      css: {
        list: {
          paddingInline: '4',
        },
        trigger: {
          textStyle: 'body/md/semibold',
          paddingInline: '12',
          paddingBlockStart: rem(10),
          paddingBlockEnd: rem(7),
        },
        moreTrigger: {
          textStyle: 'body/md/semibold',
          paddingInline: '12',
          paddingBlockStart: rem(10),
          paddingBlockEnd: rem(7),
        },
      },
    },
  ],

  defaultVariants: {
    size: 'lg',
    variant: 'line',
  },
});

export default tabsSlotRecipe;
